<script setup lang="ts">
import type { CmsUiCardItem } from '@mop/cms/types';
import type { CmsUiCardCarousel } from './uiCmsCard';
defineOptions({
  name: 'UiCmsCardItem',
});

const props = defineProps({
  data: {
    type: Object as PropType<CmsUiCardItem>,
    required: true,
  },
  lazyLoad: {
    type: Boolean,
    default: false,
  },
  appearance: {
    type: String as PropType<CmsUiCardCarousel['appearance']>,
    required: false,
    default: undefined,
  },
});

const {
  align,
  textAlign,
  headline,
  cta,
  textColor = '',
  backgroundColor = '',
  imageType,
  media,
  minHeight,
  badge,
} = props.data;

const subline = props.data.subline || '';
const body = props.data.body || '';
let headlineFont = props.data.headlineFont;

if (media) {
  media.imageType = imageType;
}

// Legacy fallback, remove after content fully migrated
if (!headlineFont) {
  headlineFont = 'h2';
}
</script>

<template>
  <UiNuxtLink
    v-storyblok-editable="data"
    v-bind="data.link"
    :class="[
      'ui-cms-card-item',
      {
        'ui-cms-card-item--split': props.appearance === 'split-image',
        'ui-cms-card-item--has-badge': badge?.label,
      },
    ]"
    :style="{
      '--color': textColor,
      '--background-color': backgroundColor,
      '--min-height': minHeight ? `${minHeight}px` : undefined,
    }"
  >
    <div v-if="media" class="ui-cms-card-item__image">
      <UiCmsMedia :data="media" :lazy="lazyLoad" :background="textColor === '#ffffff' ? 'dark' : 'light'" />
      <UiCmsBadge v-if="badge" :data="badge" class="ui-cms-card-item__badge" />
    </div>
    <div
      v-if="headline.text || subline || body || cta"
      :class="[
        'ui-cms-card-item__content',
        {
          'ui-cms-card-item__content--align-center': align === 'center',
          'ui-cms-card-item__content--align-top': align === 'top',
          'ui-cms-card-item__content--text-align-center': textAlign === 'center',
          'ui-cms-card-item__content--no-media': backgroundColor && !media,
        },
      ]"
    >
      <Ui2Heading
        v-if="headline.text"
        :level="headline.level"
        :font="headlineFont"
        :is-uppercase="headline.isUppercase || false"
      >
        {{ headline.text }}
      </Ui2Heading>
      <div v-if="subline" class="ui-cms-card-item__subline">
        {{ subline }}
      </div>
      <div v-if="body" class="ui-cms-card-item__text" v-html="body" />
      <div v-if="cta" class="ui-cms-card-item__cta">
        <UiCmsCta :data="cta" no-padding />
      </div>
    </div>
  </UiNuxtLink>
</template>

<style lang="scss" scoped>
.ui-cms-card-item {
  display: flex;
  flex-direction: column;
  position: relative;
  flex-basis: 100%;
  height: 100%;
  background-color: var(--background-color, transparent);
  color: var(--color);
}

.ui-cms-card-item__image {
  flex-shrink: 0;
  position: relative;
}

.ui-cms-card-item__badge {
  position: absolute;
  top: $space-16;
  right: $space-16;
}

.ui-cms-card-item__content {
  position: absolute;
  bottom: 0;
  display: flex;
  flex-direction: column;
  padding: $space-16;
  width: 100%;
  height: 100%;
  justify-content: flex-end;

  @include v2-apply-only(mobile) {
    width: 75%;
  }
}

.ui-cms-card-item__content--align-center {
  justify-content: center;
}

.ui-cms-card-item__content--text-align-center {
  text-align: center;
}

.ui-cms-card-item__content--no-media {
  position: relative;
  min-height: var(--min-height, auto);
}

.ui-cms-card-item__subline {
  @include v2-text-style('xl');
  @include v2-apply-upto('tablet') {
    @include v2-text-style('lg');
  }
}

.ui-cms-card-item__cta {
  margin-top: $space-16;

  @include v2-apply-upto(mobile) {
    margin-top: $space-8;
  }
}

.ui-cms-card-item--split {
  .ui-cms-card-item__content {
    position: relative;
    padding-bottom: $space-24;
  }

  .ui-cms-card-item__content.ui-cms-card-item__content--align-top {
    justify-content: flex-start;
  }

  .ui-cms-card-item__badge {
    bottom: $space-16;
    top: auto;
  }
}

.ui-cms-card-item--has-badge :deep(.video-player__control-small) {
  left: $space-16;
  right: auto;
}
</style>
